import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Phone,
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  User,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  ArrowRight,
  PhoneCall,
  PhoneOff,
  CalendarCheck,
  CalendarX,
  Shield,
  Trash2,
  Download,
  Upload,
  FileSpreadsheet,
  Loader2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATI_CONTATTO = [
  { value: 'da_chiamare', label: 'Da Chiamare', color: 'bg-slate-100 text-slate-700', icon: Phone },
  { value: 'da_richiamare', label: 'Da Richiamare', color: 'bg-amber-100 text-amber-700', icon: PhoneCall },
  { value: 'appuntamento_fissato', label: 'Appuntamento Fissato', color: 'bg-emerald-100 text-emerald-700', icon: CalendarCheck },
  { value: 'da_riprogrammare', label: 'Da Riprogrammare', color: 'bg-sky-100 text-sky-700', icon: CalendarX },
  { value: 'non_interessato', label: 'Non Interessato', color: 'bg-red-100 text-red-700', icon: PhoneOff },
];

const CallCenter = () => {
  const { user, hasRole } = useAuth();
  const [contatti, setContatti] = useState([]);
  const [operatori, setOperatori] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtroStato, setFiltroStato] = useState('');
  const [filtroOperatore, setFiltroOperatore] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedContatto, setSelectedContatto] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [exporting, setExporting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    telefono: '',
    email: '',
    indirizzo: '',
    citta: '',
    provincia: '',
    note: '',
    operatore_id: ''
  });

  const isOperatore = user?.ruolo === 'operatore_call';
  const canCreate = hasRole(['super_admin', 'admin', 'sub_agent']);
  const canDelete = hasRole(['super_admin', 'admin']);

  const fetchContatti = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filtroStato) params.append('stato', filtroStato);
      if (filtroOperatore) params.append('operatore_id', filtroOperatore);
      
      const res = await axios.get(`${API_URL}/api/callcenter/contatti?${params}`);
      setContatti(res.data);
    } catch (e) {
      console.error('Error fetching contatti:', e);
      toast.error('Errore nel caricamento contatti');
    } finally {
      setLoading(false);
    }
  }, [filtroStato, filtroOperatore]);

  const fetchOperatori = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/callcenter/operatori`);
      setOperatori(res.data);
    } catch (e) {
      console.error('Error fetching operatori:', e);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/callcenter/stats`);
      setStats(res.data);
    } catch (e) {
      console.error('Error fetching stats:', e);
    }
  };

  useEffect(() => {
    fetchContatti();
    fetchOperatori();
    fetchStats();
  }, [fetchContatti]);

  const handleCreateContatto = async () => {
    if (!formData.nome || !formData.cognome || !formData.telefono) {
      toast.error('Nome, cognome e telefono sono obbligatori');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/callcenter/contatti`, formData);
      toast.success('Contatto creato con successo');
      setShowCreateModal(false);
      resetForm();
      fetchContatti();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const handleUpdateContatto = async () => {
    if (!selectedContatto) return;
    
    try {
      await axios.put(`${API_URL}/api/callcenter/contatti/${selectedContatto.id}`, formData);
      toast.success('Contatto aggiornato');
      setShowEditModal(false);
      setSelectedContatto(null);
      resetForm();
      fetchContatti();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nell\'aggiornamento');
    }
  };

  const handleDeleteContatto = async (contattoId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo contatto?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/callcenter/contatti/${contattoId}`);
      toast.success('Contatto eliminato');
      fetchContatti();
      fetchStats();
    } catch (e) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleConvertiLead = async (contattoId) => {
    try {
      const res = await axios.post(`${API_URL}/api/callcenter/contatti/${contattoId}/converti-lead`);
      toast.success('Contatto convertito in Lead Caldo!');
      fetchContatti();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella conversione');
    }
  };

  const handleQuickStatusUpdate = async (contattoId, newStato, extraData = {}) => {
    try {
      await axios.put(`${API_URL}/api/callcenter/contatti/${contattoId}`, {
        stato: newStato,
        ...extraData
      });
      toast.success('Stato aggiornato');
      fetchContatti();
      fetchStats();
    } catch (e) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleExport = async (formato = 'xlsx') => {
    setExporting(true);
    try {
      const response = await axios.get(`${API_URL}/api/callcenter/export?formato=${formato}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contatti_callcenter_${new Date().toISOString().slice(0,10)}.${formato}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export completato');
    } catch (e) {
      toast.error('Errore nell\'export');
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/callcenter/template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_contatti_callcenter.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Template scaricato');
    } catch (e) {
      toast.error('Errore nel download template');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    setImportResult(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API_URL}/api/callcenter/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResult(response.data);
      toast.success(`Import completato: ${response.data.created} contatti creati`);
      fetchContatti();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nell\'import');
      setImportResult({ error: e.response?.data?.detail || 'Errore' });
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const openEditModal = (contatto) => {
    setSelectedContatto(contatto);
    setFormData({
      nome: contatto.nome || '',
      cognome: contatto.cognome || '',
      telefono: contatto.telefono || '',
      email: contatto.email || '',
      indirizzo: contatto.indirizzo || '',
      citta: contatto.citta || '',
      provincia: contatto.provincia || '',
      note: contatto.note || '',
      operatore_id: contatto.operatore_id || '',
      stato: contatto.stato || 'da_chiamare',
      consenso_gdpr: contatto.consenso_gdpr || false,
      data_prossima_chiamata: contatto.data_prossima_chiamata || '',
      esito_chiamata: contatto.esito_chiamata || ''
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      cognome: '',
      telefono: '',
      email: '',
      indirizzo: '',
      citta: '',
      provincia: '',
      note: '',
      operatore_id: ''
    });
  };

  const getStatoStyle = (stato) => {
    return STATI_CONTATTO.find(s => s.value === stato) || STATI_CONTATTO[0];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const filteredContatti = contatti.filter(c => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      c.nome?.toLowerCase().includes(search) ||
      c.cognome?.toLowerCase().includes(search) ||
      c.telefono?.includes(search) ||
      c.email?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="callcenter-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Call Center</h1>
          <p className="text-slate-500 mt-1">Gestione contatti e chiamate</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Export Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={exporting} data-testid="export-btn">
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Esporta
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleExport('xlsx')}>
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Esporta Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('csv')}>
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Esporta CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Import Button */}
          {canCreate && (
            <Button variant="outline" onClick={() => setShowImportModal(true)} data-testid="import-btn">
              <Upload className="w-4 h-4 mr-2" />
              Importa
            </Button>
          )}
          
          {canCreate && (
            <Button onClick={() => setShowCreateModal(true)} data-testid="create-contatto-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Contatto
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="stat-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500">Totale</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.totale}</p>
                </div>
                <Phone className="w-8 h-8 text-slate-300" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="stat-card border-l-4 border-l-slate-400">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500">Da Chiamare</p>
                  <p className="text-2xl font-bold text-slate-700">{stats.da_chiamare}</p>
                </div>
                <Phone className="w-8 h-8 text-slate-300" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="stat-card border-l-4 border-l-amber-400">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500">Da Richiamare</p>
                  <p className="text-2xl font-bold text-amber-600">{stats.da_richiamare}</p>
                </div>
                <PhoneCall className="w-8 h-8 text-amber-300" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="stat-card border-l-4 border-l-emerald-400">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500">Appuntamenti</p>
                  <p className="text-2xl font-bold text-emerald-600">{stats.appuntamento_fissato}</p>
                </div>
                <CalendarCheck className="w-8 h-8 text-emerald-300" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="stat-card border-l-4 border-l-red-400">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500">Non Interessati</p>
                  <p className="text-2xl font-bold text-red-600">{stats.non_interessato}</p>
                </div>
                <PhoneOff className="w-8 h-8 text-red-300" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Cerca per nome, telefono, email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-contatti"
                />
              </div>
            </div>
            
            <Select value={filtroStato || "all"} onValueChange={(v) => setFiltroStato(v === "all" ? "" : v)}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                {STATI_CONTATTO.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {!isOperatore && operatori.length > 0 && (
              <Select value={filtroOperatore || "all"} onValueChange={(v) => setFiltroOperatore(v === "all" ? "" : v)}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Operatore" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli operatori</SelectItem>
                  {operatori.map(o => (
                    <SelectItem key={o.id} value={o.id}>{o.nome} {o.cognome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Contacts Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="table-header">
                  <th className="text-left px-4 py-3">Contatto</th>
                  <th className="text-left px-4 py-3">Telefono</th>
                  <th className="text-left px-4 py-3">Stato</th>
                  <th className="text-left px-4 py-3">GDPR</th>
                  <th className="text-left px-4 py-3">Operatore</th>
                  <th className="text-left px-4 py-3">Prossima Chiamata</th>
                  <th className="text-left px-4 py-3">Creato</th>
                  <th className="text-right px-4 py-3">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filteredContatti.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-slate-500">
                      Nessun contatto trovato
                    </td>
                  </tr>
                ) : (
                  filteredContatti.map((contatto) => {
                    const statoInfo = getStatoStyle(contatto.stato);
                    return (
                      <tr key={contatto.id} className="table-row" data-testid={`contatto-row-${contatto.id}`}>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-slate-900">{contatto.nome} {contatto.cognome}</p>
                            {contatto.email && <p className="text-xs text-slate-500">{contatto.email}</p>}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <a href={`tel:${contatto.telefono}`} className="text-sky-600 hover:underline font-mono">
                            {contatto.telefono}
                          </a>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${statoInfo.color}`}>
                            <statoInfo.icon className="w-3 h-3" />
                            {statoInfo.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {contatto.consenso_gdpr ? (
                            <div className="flex items-center gap-1 text-emerald-600">
                              <Shield className="w-4 h-4" />
                              <span className="text-xs">OK</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-slate-400">
                              <Shield className="w-4 h-4" />
                              <span className="text-xs">No</span>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {contatto.operatore_nome || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {contatto.data_prossima_chiamata ? (
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4 text-slate-400" />
                              {formatDate(contatto.data_prossima_chiamata)}
                            </div>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          <div>
                            <p>{formatDate(contatto.created_at)}</p>
                            <p className="text-xs text-slate-400">{contatto.creato_da_nome}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditModal(contatto)}>
                                <User className="w-4 h-4 mr-2" />
                                Modifica
                              </DropdownMenuItem>
                              
                              <DropdownMenuSeparator />
                              
                              {contatto.stato !== 'da_richiamare' && (
                                <DropdownMenuItem onClick={() => handleQuickStatusUpdate(contatto.id, 'da_richiamare')}>
                                  <PhoneCall className="w-4 h-4 mr-2 text-amber-500" />
                                  Da Richiamare
                                </DropdownMenuItem>
                              )}
                              
                              {contatto.stato !== 'da_riprogrammare' && (
                                <DropdownMenuItem onClick={() => handleQuickStatusUpdate(contatto.id, 'da_riprogrammare')}>
                                  <CalendarX className="w-4 h-4 mr-2 text-sky-500" />
                                  Da Riprogrammare
                                </DropdownMenuItem>
                              )}
                              
                              {contatto.stato !== 'non_interessato' && (
                                <DropdownMenuItem onClick={() => handleQuickStatusUpdate(contatto.id, 'non_interessato')}>
                                  <PhoneOff className="w-4 h-4 mr-2 text-red-500" />
                                  Non Interessato
                                </DropdownMenuItem>
                              )}
                              
                              <DropdownMenuSeparator />
                              
                              {contatto.consenso_gdpr && !contatto.lead_id && (
                                <DropdownMenuItem onClick={() => handleConvertiLead(contatto.id)} className="text-emerald-600">
                                  <ArrowRight className="w-4 h-4 mr-2" />
                                  Converti in Lead
                                </DropdownMenuItem>
                              )}
                              
                              {contatto.lead_id && (
                                <DropdownMenuItem disabled className="text-slate-400">
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Già convertito
                                </DropdownMenuItem>
                              )}
                              
                              {canDelete && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => handleDeleteContatto(contatto.id)} className="text-red-600">
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Elimina
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create Contact Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuovo Contatto Call Center</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input
                  value={formData.nome}
                  onChange={(e) => setFormData({...formData, nome: e.target.value})}
                  placeholder="Mario"
                  data-testid="input-nome"
                />
              </div>
              <div className="space-y-2">
                <Label>Cognome *</Label>
                <Input
                  value={formData.cognome}
                  onChange={(e) => setFormData({...formData, cognome: e.target.value})}
                  placeholder="Rossi"
                  data-testid="input-cognome"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Telefono *</Label>
                <Input
                  value={formData.telefono}
                  onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                  placeholder="333 1234567"
                  data-testid="input-telefono"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="mario@email.it"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Indirizzo</Label>
              <Input
                value={formData.indirizzo}
                onChange={(e) => setFormData({...formData, indirizzo: e.target.value})}
                placeholder="Via Roma 123"
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Città</Label>
                <Input
                  value={formData.citta}
                  onChange={(e) => setFormData({...formData, citta: e.target.value})}
                  placeholder="Milano"
                />
              </div>
              <div className="space-y-2">
                <Label>Provincia</Label>
                <Input
                  value={formData.provincia}
                  onChange={(e) => setFormData({...formData, provincia: e.target.value})}
                  placeholder="MI"
                  maxLength={2}
                />
              </div>
            </div>
            
            {operatori.length > 0 && (
              <div className="space-y-2">
                <Label>Assegna a Operatore</Label>
                <Select 
                  value={formData.operatore_id || "none"} 
                  onValueChange={(v) => setFormData({...formData, operatore_id: v === "none" ? "" : v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona operatore" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Non assegnato</SelectItem>
                    {operatori.map(o => (
                      <SelectItem key={o.id} value={o.id}>{o.nome} {o.cognome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Note</Label>
              <textarea
                className="w-full h-20 px-3 py-2 text-sm border rounded-md border-slate-300"
                value={formData.note}
                onChange={(e) => setFormData({...formData, note: e.target.value})}
                placeholder="Note aggiuntive..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateModal(false); resetForm(); }}>
              Annulla
            </Button>
            <Button onClick={handleCreateContatto} data-testid="submit-contatto-btn">
              Crea Contatto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Contact Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifica Contatto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input
                  value={formData.nome}
                  onChange={(e) => setFormData({...formData, nome: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Cognome *</Label>
                <Input
                  value={formData.cognome}
                  onChange={(e) => setFormData({...formData, cognome: e.target.value})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Telefono *</Label>
                <Input
                  value={formData.telefono}
                  onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Stato</Label>
              <Select 
                value={formData.stato} 
                onValueChange={(v) => setFormData({...formData, stato: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATI_CONTATTO.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-lg">
              <Checkbox
                id="consenso_gdpr"
                checked={formData.consenso_gdpr}
                onCheckedChange={(checked) => setFormData({...formData, consenso_gdpr: checked})}
              />
              <Label htmlFor="consenso_gdpr" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-600" />
                Consenso GDPR ottenuto
              </Label>
            </div>
            
            {(formData.stato === 'da_richiamare' || formData.stato === 'da_riprogrammare') && (
              <div className="space-y-2">
                <Label>Data Prossima Chiamata</Label>
                <Input
                  type="date"
                  value={formData.data_prossima_chiamata}
                  onChange={(e) => setFormData({...formData, data_prossima_chiamata: e.target.value})}
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Esito Chiamata</Label>
              <textarea
                className="w-full h-20 px-3 py-2 text-sm border rounded-md border-slate-300"
                value={formData.esito_chiamata}
                onChange={(e) => setFormData({...formData, esito_chiamata: e.target.value})}
                placeholder="Descrivi l'esito della chiamata..."
              />
            </div>
            
            {!isOperatore && operatori.length > 0 && (
              <div className="space-y-2">
                <Label>Assegna a Operatore</Label>
                <Select 
                  value={formData.operatore_id || "none"} 
                  onValueChange={(v) => setFormData({...formData, operatore_id: v === "none" ? "" : v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona operatore" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Non assegnato</SelectItem>
                    {operatori.map(o => (
                      <SelectItem key={o.id} value={o.id}>{o.nome} {o.cognome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Note</Label>
              <textarea
                className="w-full h-20 px-3 py-2 text-sm border rounded-md border-slate-300"
                value={formData.note}
                onChange={(e) => setFormData({...formData, note: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditModal(false); setSelectedContatto(null); resetForm(); }}>
              Annulla
            </Button>
            <Button onClick={handleUpdateContatto}>
              Salva Modifiche
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Importa Contatti da Excel/CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-slate-50 rounded-lg space-y-3">
              <h4 className="font-medium text-slate-900">Formato richiesto:</h4>
              <p className="text-sm text-slate-600">
                Il file deve contenere almeno la colonna <strong>Telefono</strong>. 
                Altre colonne supportate: Nome, Cognome, Email, Indirizzo, Citta, Provincia, Note.
              </p>
              <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                <Download className="w-4 h-4 mr-2" />
                Scarica Template Excel
              </Button>
            </div>
            
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
              {importing ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
                  <p className="text-sm text-slate-600">Importazione in corso...</p>
                </div>
              ) : (
                <>
                  <FileSpreadsheet className="w-12 h-12 mx-auto text-slate-400 mb-3" />
                  <p className="text-sm text-slate-600 mb-3">
                    Trascina qui il file o clicca per selezionare
                  </p>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleImport}
                    className="hidden"
                    id="import-file"
                  />
                  <label htmlFor="import-file">
                    <Button variant="outline" asChild>
                      <span>
                        <Upload className="w-4 h-4 mr-2" />
                        Seleziona File
                      </span>
                    </Button>
                  </label>
                </>
              )}
            </div>
            
            {importResult && (
              <div className={`p-4 rounded-lg ${importResult.error ? 'bg-red-50' : 'bg-emerald-50'}`}>
                {importResult.error ? (
                  <p className="text-sm text-red-700">{importResult.error}</p>
                ) : (
                  <>
                    <p className="font-medium text-emerald-700 mb-2">Import completato!</p>
                    <ul className="text-sm text-emerald-600 space-y-1">
                      <li>Contatti creati: <strong>{importResult.created}</strong></li>
                      <li>Contatti saltati (duplicati): <strong>{importResult.skipped}</strong></li>
                    </ul>
                    {importResult.errors?.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-emerald-200">
                        <p className="text-xs text-amber-600 font-medium">Errori:</p>
                        {importResult.errors.map((err, i) => (
                          <p key={i} className="text-xs text-amber-600">{err}</p>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowImportModal(false); setImportResult(null); }}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CallCenter;
